/**
 * Edição de template de e-mail.
 *
 * Salvar/Remover no topo (padrão do painel); nome, gatilho e assunto
 * são obrigatórios no banco — marcados no formulário para o erro
 * aparecer antes do salvar. O gatilho é escolhido numa lista: os 3
 * gatilhos reais do servidor, com os usados por OUTROS templates
 * bloqueados (o banco aceita um por gatilho); um valor gravado fora da
 * lista entra como opção própria — assim não é apagado ao salvar.
 */
import {
  EditContextProvider,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  useEditController,
  useGetList,
} from "react-admin";
import { AppDeleteButton } from "../../components/AppDeleteButton";
import { useParams } from "react-router-dom";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Grid from "@mui/material/Grid";
import { RichTextInput } from "ra-input-rich-text";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import { determineUserReadableRole } from "../users/UserUtils";
import { mailTriggers } from "./mail-templates-util";

export const MailTemplateEdit = (props: any) => {
  const { id } = useParams();
  const controller = useEditController({
    resource: "mail-templates/crud",
    mutationMode: "pessimistic",
    id,
  });
  const isAdmin =
    determineUserReadableRole(localStorage.getItem("roles")) === "admin";

  // Gatilhos ocupados por OUTROS templates ficam bloqueados; o deste
  // registro permanece selecionável
  const { data: existing } = useGetList("mail-templates/crud", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });
  const takenByOthers = new Set(
    (existing ?? [])
      .filter((t: any) => String(t.id) !== String(id))
      .map((t: any) => String(t.trigger))
      .filter(Boolean)
  );
  const currentTrigger = controller?.record?.trigger;
  const triggerChoices: any[] = Object.entries(mailTriggers).map(
    ([code, info]) => ({
      id: code,
      name: takenByOthers.has(code)
        ? `${info.label} — ${code} (em uso por outro template)`
        : `${info.label} — ${code}`,
      disabled: takenByOthers.has(code),
    })
  );
  if (currentTrigger && !mailTriggers[currentTrigger]) {
    triggerChoices.push({
      id: currentTrigger,
      name: `${currentTrigger} (sem disparo no servidor)`,
    });
  }

  const TopActions = () => (
    <>
      {isAdmin && <AppDeleteButton label="Remover" />}
      <SaveButton type="button" label="Salvar" />
    </>
  );

  return (
    <EditContextProvider value={controller}>
      {/* toolbar={false}: Salvar/Remover saem do rodapé e vão para o topo */}
      <SimpleForm toolbar={false}>
        <PageTopBreadcrumb
          title="Templates de E-mail"
          firstPath="/#/mail-templates/crud"
          secondTitle="Editando"
          thirdTitle={controller && controller.record && controller.record.name}
          actionType="show"
          actions={<TopActions />}
        />
        <Card sx={{ padding: 2, marginBottom: 3, width: "100%" }}>
          <CardHeader title="Dados do Template" />
          <CardContent>
            <Grid container columnSpacing={2} rowSpacing={0.5}>
              <Grid item xs={12}>
                {/* Chave do registro: protegida contra edição acidental */}
                <TextInput
                  source="id"
                  label="ID"
                  fullWidth
                  helperText={false}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="name"
                  label="Nome"
                  fullWidth
                  helperText={false}
                  validate={required()}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SelectInput
                  source="trigger"
                  label="Gatilho"
                  choices={triggerChoices}
                  fullWidth
                  validate={required()}
                  helperText="Um template por gatilho — os usados por outros templates aparecem bloqueados"
                />
              </Grid>
              <Grid item xs={12}>
                <TextInput
                  source="subject"
                  label="Assunto"
                  fullWidth
                  helperText={false}
                  validate={required()}
                />
              </Grid>
              <Grid item xs={12}>
                <div className="richTextBody">
                  <RichTextInput
                    source="body"
                    label="Corpo do e-mail"
                    fullWidth
                    helperText={false}
                    validate={required()}
                  />
                </div>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </SimpleForm>
    </EditContextProvider>
  );
};
